import React, { useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import Section from '@hero/ui-kit/layout/Section';
import useTheme from '@hero/branding/theme';
import {
    MarkButton,
    MarkChoice,
    MarkChoiceLabel,
    MarkInput,
    MarkLabel
} from '../../../components/MarkControls';
import type { SegmentFilterOperator, SegmentAttributeType } from '../../../types';
import { stripEndOfDay, toEndOfDay } from './validation';
import * as styles from './style.module.css';

type ValueProps = {
    values: (string | boolean)[];
    onChange: (values: (string | boolean)[]) => void;
    idPrefix: string;
    labelId: string;
    onPendingChange?: (hasPending: boolean) => void;
};
type Props = ValueProps & {
    operator: SegmentFilterOperator;
    attributeType: SegmentAttributeType;
    attributeValues: string[] | null;
};

const BooleanInput: React.FC<ValueProps & { isNull?: boolean }> = ({
    values,
    onChange,
    idPrefix,
    labelId,
    isNull
}) => {
    // Keep compatibility with older saved filters containing "true"/"false" strings.
    const raw = values[0];
    const current =
        typeof raw === 'boolean' ? raw : raw === 'true' ? true : raw === 'false' ? false : null;
    return (
        <div
            id={idPrefix}
            role="radiogroup"
            aria-labelledby={labelId}
            tabIndex={-1}
            className={styles.valuesGrid}
        >
            {[true, false].map((option) => (
                <MarkChoiceLabel key={String(option)} htmlFor={`${idPrefix}-${option}`}>
                    <MarkChoice
                        id={`${idPrefix}-${option}`}
                        type="radio"
                        name={idPrefix}
                        value={String(option)}
                        checked={current === option}
                        onChange={() => onChange([option])}
                    />
                    {isNull ? (option ? 'Is empty' : 'Is not empty') : option ? 'True' : 'False'}
                </MarkChoiceLabel>
            ))}
        </div>
    );
};

const SingleInput: React.FC<ValueProps & { type: 'text' | 'date' }> = ({
    type,
    values,
    onChange,
    idPrefix,
    labelId
}) => (
    <MarkInput
        id={idPrefix}
        aria-labelledby={labelId}
        type={type}
        value={typeof values[0] === 'string' ? values[0] : ''}
        onChange={(event) => onChange([event.currentTarget.value])}
        style={{ width: '100%' }}
        placeholder={type === 'text' ? 'Enter text to match' : undefined}
    />
);

const BetweenInput: React.FC<ValueProps> = ({ values, onChange, idPrefix, labelId }) => {
    const { colors } = useTheme();
    const start = typeof values[0] === 'string' ? values[0] : '';
    const end = typeof values[1] === 'string' ? values[1] : '';
    return (
        <>
            <div className={styles.valuesGrid} aria-labelledby={labelId}>
                <div className={styles.field}>
                    <MarkLabel htmlFor={`${idPrefix}-start`} style={{ margin: 0 }}>
                        From
                    </MarkLabel>
                    <MarkInput
                        id={`${idPrefix}-start`}
                        type="date"
                        value={start}
                        onChange={(event) => onChange([event.currentTarget.value, end])}
                        style={{ width: '100%' }}
                    />
                </div>
                <div className={styles.field}>
                    <MarkLabel htmlFor={`${idPrefix}-end`} style={{ margin: 0 }}>
                        Through
                    </MarkLabel>
                    <MarkInput
                        id={`${idPrefix}-end`}
                        type="date"
                        value={stripEndOfDay(end)}
                        onChange={(event) =>
                            onChange([start, toEndOfDay(event.currentTarget.value)])
                        }
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
            <P noDefaultMargin size="small" styles={{ color: colors.neutrals.textBeta }}>
                The end date is included.
            </P>
        </>
    );
};

const CheckboxListInput: React.FC<ValueProps & { options: string[] }> = ({
    options,
    values,
    onChange,
    idPrefix,
    labelId
}) => {
    const { colors, borders } = useTheme();
    const stringValues = values.filter((value): value is string => typeof value === 'string');
    return (
        <div
            id={idPrefix}
            role="group"
            aria-labelledby={labelId}
            tabIndex={-1}
            className={styles.valueOptions}
        >
            {options.map((option, index) => {
                const checked = stringValues.includes(option);
                return (
                    <Section
                        key={option}
                        border
                        padding="small"
                        styles={{
                            ...(checked ? borders.variant.primary : borders.variant.secondary),
                            backgroundColor: checked
                                ? colors.brand.primary.epsilon
                                : colors.neutrals.background
                        }}
                    >
                        <MarkLabel
                            htmlFor={`${idPrefix}-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.8rem',
                                margin: 0
                            }}
                        >
                            <MarkChoice
                                id={`${idPrefix}-${index}`}
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                    onChange(
                                        checked
                                            ? stringValues.filter((value) => value !== option)
                                            : [...stringValues, option]
                                    )
                                }
                            />
                            {option}
                        </MarkLabel>
                    </Section>
                );
            })}
        </div>
    );
};

const ChipsInput: React.FC<ValueProps> = ({
    values,
    onChange,
    idPrefix,
    labelId,
    onPendingChange
}) => {
    const [input, setInput] = useState('');
    const { colors, borders } = useTheme();
    const stringValues = values.filter((value): value is string => typeof value === 'string');
    const addValue = (text: string) => {
        const trimmed = text.trim();
        if (trimmed && !stringValues.includes(trimmed)) onChange([...stringValues, trimmed]);
        setInput('');
        onPendingChange?.(false);
    };
    return (
        <div className={styles.field}>
            <div className={styles.valueEntry}>
                <MarkInput
                    id={idPrefix}
                    aria-labelledby={labelId}
                    aria-describedby={`${idPrefix}-hint`}
                    value={input}
                    type="text"
                    onChange={(event) => {
                        setInput(event.currentTarget.value);
                        onPendingChange?.(!!event.currentTarget.value.trim());
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ',') {
                            event.preventDefault();
                            addValue(input);
                        }
                    }}
                    placeholder="Enter a value"
                    style={{ width: '100%' }}
                />
                <MarkButton
                    onClick={() => addValue(input)}
                    disabled={!input.trim()}
                    variant="outlineSecondary"
                >
                    Add value
                </MarkButton>
            </div>
            <P
                id={`${idPrefix}-hint`}
                noDefaultMargin
                size="small"
                styles={{ color: colors.neutrals.textBeta }}
            >
                Add one or more values. Press Enter or use Add value after each entry.
            </P>
            {stringValues.length > 0 && (
                <div className={styles.chips} aria-label="Selected values">
                    {stringValues.map((value) => (
                        <span
                            key={value}
                            className={styles.chip}
                            style={{
                                ...borders.shape.rounded,
                                backgroundColor: colors.brand.primary.epsilon,
                                padding: '0.4rem 1.2rem'
                            }}
                        >
                            <span>{value}</span>
                            <MarkButton
                                onClick={() =>
                                    onChange(
                                        stringValues.filter((candidate) => candidate !== value)
                                    )
                                }
                                aria-label={`Remove value ${value}`}
                                variant="secondary"
                            >
                                ×
                            </MarkButton>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const ValuesInput: React.FC<Props> = ({ operator, attributeType, attributeValues, ...props }) => {
    if (operator === 'is_null') return <BooleanInput {...props} isNull />;
    if (operator === 'after' || operator === 'before')
        return <SingleInput {...props} type="date" />;
    if (operator === 'contains') return <SingleInput {...props} type="text" />;
    if (operator === 'between') return <BetweenInput {...props} />;
    if (attributeType === 'boolean') return <BooleanInput {...props} />;
    if (attributeType === 'enum' && attributeValues)
        return <CheckboxListInput {...props} options={attributeValues} />;
    return <ChipsInput {...props} />;
};
export default ValuesInput;
